import { useEffect, useState, useCallback, useRef, useSyncExternalStore } from "react";
import { api, fail, errMsg } from "@/lib/api";
import { apiLoadSnapshot, subscribeApiLoads, setApiLoadState } from "@/lib/loadStatus";

const cx = (...a) => a.filter(Boolean).join(" ");
export { cx };

let nextApiId = 0;
const waitForReadRetry = (delay, signal) => new Promise(resolve => {
  const done = () => { clearTimeout(timer); signal.removeEventListener("abort", done); resolve(); };
  const timer = setTimeout(done, delay);
  signal.addEventListener("abort", done, { once: true });
});

export function useApi(url, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const id = useRef(null);
  if (id.current === null) id.current = ++nextApiId;
  const current = useRef(0);
  const controller = useRef(null);
  const cancelRead = useCallback(() => { ++current.current; controller.current?.abort(); }, []);
  const load = useCallback(async () => {
    if (!url) return;
    controller.current?.abort();
    const abort = new AbortController(); controller.current = abort;
    const epoch = ++current.current;
    setLoading(true);
    setError(null);
    setApiLoadState(id.current, { loading: true });
    let failure = null;
    try {
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const response = await api.get(url, { signal: abort.signal });
          if (epoch !== current.current || abort.signal.aborted) return;
          setData(response.data);
          return response.data;
        } catch (e) {
          if (epoch !== current.current || abort.signal.aborted || e?.code === "ERR_CANCELED") return;
          const transient = !e?.response || e.response.status >= 500;
          if (!transient || attempt === 2) { failure = e; break; }
          await waitForReadRetry(attempt === 0 ? 800 : 1800, abort.signal);
          if (epoch !== current.current || abort.signal.aborted) return;
        }
      }
      setError(failure);
      fail(failure);
    } finally {
      if (epoch === current.current && !abort.signal.aborted) {
        setLoading(false);
        setApiLoadState(id.current, failure ? { error: errMsg(failure), retry: load } : null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, ...deps]);
  useEffect(() => {
    setData(null);
    if (url) load(); else { setLoading(false); setError(null); }
    const requestId = id.current;
    return () => { cancelRead(); setApiLoadState(requestId, null); };
  }, [load, url, cancelRead]);
  return { data, loading, error, reload: load, setData };
}

export function ApiLoadStatus() {
  const status = useSyncExternalStore(subscribeApiLoads, apiLoadSnapshot, apiLoadSnapshot);
  if (!status.loading && !status.errors.length) return null;
  return <div role={status.errors.length ? "alert" : "status"} className="mb-4 rounded-xl border border-[#FFBD36]/40 p-3 text-sm" data-testid="api-load-status">
    <p>{status.errors.length ? "Certaines données n’ont pas pu être chargées. Une liste vide ici ne confirme pas l’absence de données." : "Chargement des données… Les informations affichées sont en cours de mise à jour."}</p>
    {status.errors.length > 0 && <button type="button" className="mt-2 underline" onClick={() => status.errors.forEach(item => item.retry())}>Réessayer le chargement</button>}
  </div>;
}

const V = {
  primary: "bg-[#7CFF00] text-black hover:bg-[#23E500] font-semibold",
  ghost: "border border-[rgba(124,255,0,.25)] text-white hover:border-[#7CFF00] hover:text-[#7CFF00]",
  gold: "bg-[#FFBD36] text-black hover:brightness-110 font-semibold",
  danger: "border border-[#FF453A]/50 text-[#FF453A] hover:bg-[#FF453A]/10",
};
export const Btn = ({ variant = "primary", className, size = "md", ...p }) => (
  <button {...p} className={cx("inline-flex items-center justify-center gap-2 rounded-full transition-[background-color,color,border-color,transform] duration-200 active:scale-[.97] disabled:opacity-40 disabled:pointer-events-none",
    size === "sm" ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm", V[variant], className)} />
);

export const PageHeader = ({ kicker, title, desc, children, testid }) => (
  <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 fade-up" data-testid={testid}>
    <div>
      {kicker && <div className="mono text-[11px] uppercase tracking-[.25em] text-[#7CFF00] mb-2">{kicker}</div>}
      <h1 className="text-3xl sm:text-4xl font-black tracking-tight">{title}</h1>
      {desc && <p className="text-[#A0AAB2] mt-2 max-w-2xl text-sm sm:text-base">{desc}</p>}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Card = ({ title, children, className, right, testid }) => (
  <div className={cx("card p-5 fade-up", className)} data-testid={testid}>
    {(title || right) && (
      <div className="flex items-center justify-between mb-4 gap-2">
        <h3 className="font-display font-semibold text-base sm:text-lg">{title}</h3>
        {right}
      </div>
    )}
    {children}
  </div>
);

export const NotSynced = () => <span className="text-[#626E77] text-sm italic" data-testid="not-synced">non synchronisé</span>;

export const Kpi = ({ label, value, sub, accent, testid, icon: Icon }) => (
  <div className="card card-hover p-4 fade-up" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="mono text-[10px] uppercase tracking-[.2em] text-[#A0AAB2]">{label}</span>
      {Icon && <Icon size={15} className="text-[#7CFF00]" />}
    </div>
    <div className={cx("mt-2 text-2xl font-display font-bold", accent === "gold" ? "text-[#FFBD36]" : "text-white")} data-testid={testid && `${testid}-value`}>
      {value === null || value === undefined ? <NotSynced /> : value}
    </div>
    {sub && <div className="text-xs text-[#626E77] mt-1">{sub}</div>}
  </div>
);

export const Score = ({ value, testid }) => {
  if (value === null || value === undefined) return <NotSynced />;
  const c = value >= 80 ? "#7CFF00" : value >= 50 ? "#FFBD36" : "#FF453A";
  return <span data-testid={testid} className="mono text-xs px-2 py-0.5 rounded-full border" style={{ color: c, borderColor: c + "66" }}>{Math.round(value)}/100</span>;
};

const SC = { HEALTHY: "#7CFF00", AVAILABLE: "#7CFF00", SUCCESS: "#7CFF00", LOW: "#7CFF00", DEGRADED: "#FFBD36", PARTIAL: "#FF9F0A", ASSISTANT_ONLY: "#FFBD36",
  WAITING_USER: "#FFBD36", UNVERIFIED: "#FFBD36", STALE: "#FF9F0A", MEDIUM: "#FFBD36", RETRYING: "#FF9F0A", QUEUED: "#A0AAB2", RUNNING: "#23E500", OFFLINE: "#FF453A", UNAVAILABLE: "#FF453A",
  FAILED: "#FF453A", DEAD_LETTER: "#FF453A", HIGH: "#FF453A", CRITICAL: "#FF453A", CANCELLED: "#626E77", HOT: "#7CFF00", NEGOTIATOR: "#FFBD36",
  INTERESTED: "#23E500", CURIOUS: "#A0AAB2", SUSPECT: "#FF453A", COPILOT: "#A0AAB2", ASSISTANT: "#FFBD36", AUTOPILOT: "#7CFF00" };
export const Pill = ({ value, label, testid }) => {
  const c = SC[value] || "#A0AAB2";
  return (
    <span data-testid={testid} className="mono inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border" style={{ color: c, borderColor: c + "55" }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: c }} />{label || value}
    </span>
  );
};

export const Estimate = () => <span className="mono text-[9px] uppercase tracking-widest text-[#FFBD36] border border-[#FFBD36]/40 rounded px-1.5 py-0.5">estimation</span>;

export const Empty = ({ text, children, testid }) => (
  <div className="text-center py-10 text-[#626E77] text-sm" data-testid={testid || "empty-state"}>
    <div>{text}</div>
    {children && <div className="mt-4 flex justify-center gap-2">{children}</div>}
  </div>
);

export const Field = ({ label, children }) => (
  <label className="block">
    <span className="mono text-[10px] uppercase tracking-[.18em] text-[#A0AAB2]">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

export const Table = ({ cols, rows, testid, empty = "Aucune donnée" }) => (
  <div className="overflow-x-auto" data-testid={testid}>
    {rows?.length ? (
      <table className="w-full text-sm">
        <thead><tr className="text-left">{cols.map((c) => <th key={c.k} className="mono text-[10px] uppercase tracking-widest text-[#626E77] font-normal py-2 pr-4 whitespace-nowrap">{c.l}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-t border-[rgba(124,255,0,.08)] hover:bg-white/[.02]">
              {cols.map((c) => <td key={c.k} className="py-2.5 pr-4 align-top">{c.r ? c.r(r) : r[c.k] ?? "—"}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    ) : <Empty text={empty} />}
  </div>
);

export const Tabs = ({ tabs, value, onChange }) => (
  <div className="flex gap-1 mb-6 border-b border-[rgba(124,255,0,.12)] overflow-x-auto">
    {tabs.map(([k, l]) => (
      <button key={k} data-testid={`tab-${k}`} onClick={() => onChange(k)}
        className={cx("px-4 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors", value === k ? "border-[#7CFF00] text-[#7CFF00]" : "border-transparent text-[#A0AAB2] hover:text-white")}>{l}</button>
    ))}
  </div>
);
